import { useCallback, useMemo, useState } from 'react';

import { useGetStaticAssetUrl } from '@change-corgi/core/react/assets';
import { useI18n } from '@change-corgi/core/react/i18n';
import { useTracking } from '@change-corgi/core/react/tracking';

import { usePetitionEditPageData, usePetitionEditPageState } from 'src/app/pages/petitionEditV2/pageContext';
import type { PetitionEditPageState } from 'src/app/pages/petitionEditV2/pageContext';
import type { DecisionMaker } from 'src/app/shared/hooks/decisionMakers';
import { useDecisionMakerSearch, useDecisionMakerSearchTypesense } from 'src/app/shared/hooks/decisionMakers';
import { useIsStaff } from 'src/app/shared/hooks/session';
import { isLoaded } from 'src/app/shared/utils/async';

type DecisionMakerType = PetitionEditPageState['formState']['decisionMakers']['value'][number];
type UseDecisionMakerReturn = ModelHookResult<
	{
		inputValue: string;
		options: readonly DecisionMaker[];
		label: string;
		value: DecisionMaker | null;
		selectedDecisionMakers: DecisionMakerType[];
	},
	{
		handleInputChange: (event: React.ChangeEvent<HTMLInputElement> | null, newValue: string) => void;
		handleChange: (event: React.SyntheticEvent | null, newOption: DecisionMaker | null) => void;
		handleOnRemove: (decisionMaker: DecisionMakerType) => void;
	}
>;

export function useDecisionMaker(): UseDecisionMakerReturn {
	const getStaticAssetUrl = useGetStaticAssetUrl();
	const [state, mutations] = usePetitionEditPageState();
	const {
		data: { featureConfigs },
	} = usePetitionEditPageData();
	const { translate } = useI18n();
	const track = useTracking();
	const isStaff = useIsStaff();

	const [inputValue, setInputValue] = useState('');
	const handleInputChange = (_: React.ChangeEvent<HTMLInputElement> | null, newValue: string) => {
		setInputValue(newValue);
	};

	const trackDmAddAttempt = useCallback(
		(dm: DecisionMaker) => {
			void track('petition_edit_decision_maker_add_attempt', {
				is_staff: isStaff,
				dm_id: dm.id,
			});
		},
		[track, isStaff],
	);

	const trackDmRemoveClick = useCallback(
		(dmId: string | number) => {
			void track('petition_edit_decision_maker_remove_click', {
				is_staff: isStaff,
				dm_id: dmId.toString(),
			});
		},
		[track, isStaff],
	);

	const selectedDecisionMakers = state.formState.decisionMakers.value || [];
	const [value, setValue] = useState<DecisionMaker | null>(null);
	const handleChange = (_: React.SyntheticEvent | null, selectedDM: DecisionMaker | null) => {
		if (!selectedDM) return;

		setInputValue('');
		setValue(null);

		const unknownDmId = '0';

		const formattedDM = transformSearchDecisionMaker(
			{
				...selectedDM,
				id: selectedDM.id === unknownDmId ? `temp-dm-id-${Date.now().toString(36)}` : selectedDM.id,
			},
			getStaticAssetUrl,
		);

		if (!selectedDecisionMakers.some((selectedDecisionMaker) => selectedDecisionMaker.id === selectedDM.id)) {
			const newDecisionMakers = [formattedDM, ...selectedDecisionMakers];
			mutations.setDecisionMakers(newDecisionMakers);
			trackDmAddAttempt(selectedDM);
		}
	};

	const handleOnRemove = (removedDM: DecisionMakerType) => {
		const newDecisionMakers = selectedDecisionMakers.filter((e) => e.id !== removedDM.id);
		mutations.setDecisionMakers(newDecisionMakers);
		trackDmRemoveClick(removedDM.id);
	};

	const decisionMakersResultsSearchService = useDecisionMakerSearch(
		featureConfigs.typesenseDecisionMakerSearch ? '' : inputValue,
	);
	const decisionMakersResultsTypesense = useDecisionMakerSearchTypesense(
		featureConfigs.typesenseDecisionMakerSearch ? inputValue : '',
	);
	const decisionMakersResults = featureConfigs.typesenseDecisionMakerSearch
		? decisionMakersResultsTypesense
		: decisionMakersResultsSearchService;

	const options = useMemo(() => {
		return isLoaded(decisionMakersResults) ? decisionMakersResults.data : [];
	}, [decisionMakersResults]);

	return {
		data: {
			inputValue,
			options,
			label: translate('corgi.petition.edit.petition_data.decision_maker.title'),
			value,
			selectedDecisionMakers,
		},
		actions: {
			handleInputChange,
			handleChange,
			handleOnRemove,
		},
	};
}

// transform selected DM from search API to form state
function transformSearchDecisionMaker(
	decisionMaker: DecisionMaker,
	getStaticAssetUrl: (path: string) => string,
): DecisionMakerType {
	const photoUrl = () => {
		if (decisionMaker.photo?.userSmall.url) return decisionMaker.photo.userSmall.url;

		return decisionMaker.isPerson
			? getStaticAssetUrl('/profile-img/default-user-profile.svg')
			: // FIXME find a better default avatar for orgs
				getStaticAssetUrl('/profile-img/default-org-profile.svg');
	};

	return {
		id: decisionMaker.id,
		displayName: decisionMaker.displayName,
		isConfirmed: decisionMaker.isConfirmed,
		title: decisionMaker.summary ?? null,
		isPerson: decisionMaker.isPerson ?? false,
		photoUrl: photoUrl(),
	};
}
